import React, { createContext, useContext } from 'react';
import {
  motion, AnimatePresence, useReducedMotion, useScroll, useTransform, useSpring,
} from 'motion/react';

/**
 * Camada única de animação do site.
 *
 * Substitui os três sistemas que antes disputavam os mesmos elementos
 * (ScrollReveal + IntersectionObserver manual + @keyframes no CSS). Ali um
 * elemento podia ter dois donos de opacity/transform e travava invisível; aqui
 * cada elemento tem exatamente um dono: o Motion.
 *
 * Duas diferenças de comportamento em relação ao anterior:
 * - roda também no mobile (o hook antigo saía cedo em telas ≤768px);
 * - `once: true` — revelou, acabou. Nada volta a sumir ao rolar de novo.
 */

const EASE = [0.22, 1, 0.36, 1];

/** Margem negativa embaixo: dispara um pouco antes de entrar de fato na tela. */
const VIEWPORT = { once: true, amount: 0.15, margin: '0px 0px -80px 0px' };

/* Direção → deslocamento inicial. */
const OFFSETS = {
  up:    { y: 30, x: 0 },
  down:  { y: -30, x: 0 },
  left:  { y: 0, x: 48 },
  right: { y: 0, x: -48 },
  none:  { y: 0, x: 0 },
};

/**
 * Revela um bloco ao entrar na viewport.
 *
 * @param {'up'|'down'|'left'|'right'|'none'} from  origem do deslocamento
 * @param {number} delay      atraso em segundos
 * @param {number} duration   duração em segundos
 * @param {number} scale      escala inicial (use <1 para efeito de zoom-in)
 */
export const Reveal = ({
  children,
  as = 'div',
  from = 'up',
  delay = 0,
  duration = 0.7,
  scale = 1,
  className,
  ...rest
}) => {
  const reduced = useReducedMotion();
  const Tag = motion[as] ?? motion.div;

  if (reduced) {
    const Plain = as;
    return <Plain className={className} {...rest}>{children}</Plain>;
  }

  const { x, y } = OFFSETS[from] ?? OFFSETS.up;

  return (
    <Tag
      className={className}
      initial={{ opacity: 0, x, y, scale }}
      whileInView={{ opacity: 1, x: 0, y: 0, scale: 1 }}
      viewport={VIEWPORT}
      transition={{ duration, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* O container publica seus parâmetros para que cada Item herde a mesma
   linguagem de movimento sem precisar repetir props. */
const StaggerContext = createContext({ from: 'up', duration: 0.6 });

/**
 * Container que revela os filhos em cascata.
 * Use com <StaggerItem> em cada filho direto.
 *
 * @param {number} gap  intervalo entre filhos, em segundos
 */
export const Stagger = ({
  children,
  as = 'div',
  gap = 0.09,
  delay = 0,
  from = 'up',
  duration = 0.6,
  className,
  ...rest
}) => {
  const reduced = useReducedMotion();
  const Tag = motion[as] ?? motion.div;

  if (reduced) {
    const Plain = as;
    return <Plain className={className} {...rest}>{children}</Plain>;
  }

  return (
    <StaggerContext.Provider value={{ from, duration }}>
      <Tag
        className={className}
        initial="hidden"
        whileInView="shown"
        viewport={VIEWPORT}
        variants={{
          hidden: {},
          shown: { transition: { staggerChildren: gap, delayChildren: delay } },
        }}
        {...rest}
      >
        {children}
      </Tag>
    </StaggerContext.Provider>
  );
};

export const StaggerItem = ({ children, as = 'div', className, ...rest }) => {
  const reduced = useReducedMotion();
  const { from, duration } = useContext(StaggerContext);
  const Tag = motion[as] ?? motion.div;

  if (reduced) {
    const Plain = as;
    return <Plain className={className} {...rest}>{children}</Plain>;
  }

  const { x, y } = OFFSETS[from] ?? OFFSETS.up;

  return (
    <Tag
      className={className}
      variants={{
        hidden: { opacity: 0, x, y },
        shown: { opacity: 1, x: 0, y: 0, transition: { duration, ease: EASE } },
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * Deslocamento suave conforme o elemento cruza a viewport.
 * Mantenha `distance` baixo — parallax exagerado é o que faz um site
 * parecer amador e ainda causa enjoo.
 *
 * @param {number} distance  deslocamento total em px ao longo da travessia
 */
export const Parallax = ({ children, distance = 60, className, ...rest }) => {
  const reduced = useReducedMotion();
  const ref = React.useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  /* A mola tira o "degrau" do scroll e deixa o movimento contínuo. */
  const eased = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 26,
    restDelta: 0.001,
  });

  const y = useTransform(eased, [0, 1], [distance / 2, -distance / 2]);

  if (reduced) {
    return <div ref={ref} className={className} {...rest}>{children}</div>;
  }

  return (
    <motion.div ref={ref} className={className} style={{ y }} {...rest}>
      {children}
    </motion.div>
  );
};

/**
 * Barra de progresso da leitura, fixa no topo.
 * Não recebe conteúdo — é puramente decorativa e fica fora do fluxo.
 */
export const ScrollProgress = ({ className }) => {
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });

  if (reduced) return null;

  return <motion.div className={className} style={{ scaleX }} aria-hidden="true" />;
};

export { motion, AnimatePresence, useReducedMotion };
